import { useCallback, useMemo, useRef } from 'react';
import { isEmpty, sortBy } from 'lodash';
import { AfterCellChanged, Rows } from 'common/types/scalarSpreadsheet';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import {
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_NAME,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_TABLE_TERMS,
} from 'pages/FundInvestments/common/constants';
import {
  createColumns,
  createCustomValidations,
  createParser,
  CreateParserParams,
  createReverseParser,
  createRows,
  CustomValidationsParams,
  FundInvestmentMarketAdjustmentColumn,
  ReverseParserParams,
} from './config';
import {
  FundInvestmentMarketAdjustmentCellData,
  UseFundInvestmentMarketAdjustmentSheetParams,
  UseFundInvestmentMarketAdjustmentSheetResult,
} from './useFundInvestmentMarketAdjustmentSheet.types';

const useFundInvestmentMarketAdjustmentSheet = (
  params: UseFundInvestmentMarketAdjustmentSheetParams
): UseFundInvestmentMarketAdjustmentSheetResult => {
  const {
    adjustmentOptions,
    areComparisonsSynchronized,
    asOfDate,
    companyTypesPresence,
    comparisonsByDate,
    fieldAttributes,
    forceReadOnly,
    isSingleComparison,
    marketAdjustment,
    marketCurrency,
    marketDate,
    percentileSelection,
    setMarketAdjustment,
    setMarketComparisons,
    setSynchronizedMarketAdjustment,
    uniqueComparisons,
  } = params;

  // Last successfully generated columns
  const fallbackColumnsRef = useRef<FundInvestmentMarketAdjustmentColumn[]>([]);

  const rows = useMemo<Rows>(
    () =>
      createRows({
        companyTypesPresence,
        isSingleComparison,
        marketCurrency,
        uniqueComparisons: sortBy(uniqueComparisons, 'order'),
      }),

    [companyTypesPresence, isSingleComparison, marketCurrency, uniqueComparisons]
  );

  const columns = useMemo<FundInvestmentMarketAdjustmentColumn[]>(() => {
    // If there are no comparisons, return an empty columns
    if (isEmpty(comparisonsByDate)) {
      return [];
    }

    // If comparisons are synchronized, generate and cache columns
    if (areComparisonsSynchronized) {
      const generatedColumns = createColumns({ comparisonsByDate, isSingleComparison });
      fallbackColumnsRef.current = generatedColumns;

      return generatedColumns;
    }

    // If not synchronized, fallback to last valid columns
    return fallbackColumnsRef.current;
  }, [areComparisonsSynchronized, comparisonsByDate, isSingleComparison]);

  const parser = useCallback(
    (props: CreateParserParams) =>
      createParser({
        ...props,
        adjustmentOptions,
        asOfDate,
        companyTypesPresence,
        forceReadOnly,
        isSingleComparison,
        marketAdjustment,
        marketDate,
        percentileSelection,
      }),
    [
      adjustmentOptions,
      asOfDate,
      companyTypesPresence,
      forceReadOnly,
      isSingleComparison,
      marketAdjustment,
      marketDate,
      percentileSelection,
    ]
  );

  const afterCellChanged = useCallback<AfterCellChanged<FundInvestmentMarketAdjustmentCellData>>(initialChanges => {
    const changes = [...initialChanges];

    return changes;
  }, []);

  const reverseParser = useCallback(
    (reverseParserParams: ReverseParserParams) =>
      createReverseParser({
        ...reverseParserParams,
        asOfDate,
        percentileSelection,
        setMarketAdjustment,
        setMarketComparisons,
        setSynchronizedMarketAdjustment,
      }),
    [asOfDate, percentileSelection, setMarketAdjustment, setMarketComparisons, setSynchronizedMarketAdjustment]
  );

  const customValidations = useCallback(
    (customValidationsArgs: CustomValidationsParams) => createCustomValidations(customValidationsArgs),
    []
  );

  // Spreadsheet config
  const spreadsheet = useMemo<SpreadsheetConfig | null>(() => {
    if (!isEmpty(columns)) {
      const spreadsheetConfig = new SpreadsheetConfig({
        afterCellChanged: afterCellChanged as SpreadsheetConfig['afterCellChanged'],
        allowConfirmAndDeleteColumn: false,
        allowDeleteRow: true,
        alwaysDisplayLegend: false,
        colTitleRow: 0,
        columns: columns as SpreadsheetConfig['columns'],
        currencyFormatter: true,
        customValidations: customValidations as SpreadsheetConfig['customValidations'],
        fieldAttributes,
        format: undefined,
        hasColTitle: true,
        name: FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_NAME,
        page: FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_NAME as SpreadsheetConfig['page'],
        parser: parser as unknown as SpreadsheetConfig['parser'],
        reverseParseAfterLoad: true, // Execute reverse parser when evaluating spreadsheets
        reverseParser: reverseParser as SpreadsheetConfig['reverseParser'],
        rowConfig: rows as SpreadsheetConfig['rowConfig'],
        showPreviousColsDivider: false,
        showTitlesColumn: true,
        showToolbar: true,
        showTotalColumn: false,
        tableData: { columns },
        tableTerms: FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_TABLE_TERMS as SpreadsheetConfig['tableTerms'],
        totalParser: undefined,
        unitsFormatter: true,
      });

      return spreadsheetConfig;
    }

    return null;
  }, [afterCellChanged, columns, customValidations, fieldAttributes, parser, reverseParser, rows]);

  return {
    spreadsheet,
  };
};

export default useFundInvestmentMarketAdjustmentSheet;
